import { CenteredLazyLoading } from "@/controls/LazyLoading";
import { ConfirmationDialogRaw } from "@/controls/modals/ConfirmationDialogRaw";
import { NotificationSnackBar } from "@/controls/snackbars/notification-snackbar";
import { routeStackerProduction } from "@/setup/routes/stacker-pulpit.routes";
import {
	ErrorModal,
	HelmetDexteel,
	ShiftDayNavigatorControl,
	useShiftNavigator,
	useUserContext,
} from "@dexteel/mesf-core";
import { Add } from "@mui/icons-material";
import { Box, Button, Divider, Grid2, Typography } from "@mui/material";
import { pageBgColor } from "@/utils/theme";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { get } from "lodash-es";
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { MILL_1 } from "@/setup/mill-config/mill-config";
import { StackerBundlesGrid } from "./components/StackerBundlesGrid";
import { StackerPiecesGrid } from "./components/StackerPiecesGrid";
import { StackerShiftRemarkField } from "./components/StackerShiftRemarkField";
import {
	STACKER_BUNDLE_DRAFT_INITIAL,
	STACKER_PIECES_DRAFT_INITIAL,
	StackerBundle,
	StackerBundleDraft,
} from "./models/StackerBundle";
import {
	deleteStackerBundle,
	getStackerReport,
	StackerReport,
	upsertStackerBundle,
	upsertStackerShiftRemark,
} from "./repositories/StackerPulpitRepository";

export const useStackerReport = ({
	shiftId,
}: {
	shiftId: number | undefined;
}) => {
	const {
		state: { defaultAreaId },
	} = useUserContext();

	return useQuery<StackerReport, Error>({
		queryKey: ["stacker-report", shiftId, defaultAreaId],
		queryFn: ({ signal }) =>
			getStackerReport(
				{ shiftId: shiftId || 0, defaultAreaId: Number(defaultAreaId) },
				signal,
			),
		enabled: !!shiftId,
	});
};

export const StackerPulpitPage = () => {
	const {
		state: { defaultAreaId },
	} = useUserContext();

	const isM1 = Number(defaultAreaId) === MILL_1.id;

	const queryClient = useQueryClient();
	const navigate = useNavigate();
	const { shiftId: shiftIdParam } = useParams();
	const { shiftInfo, getShiftDataFromAPI } = useShiftNavigator();
	const currentShiftId = shiftInfo?.CurrentShiftId;

	const [editingId, setEditingId] = useState<number | null>(null);
	const [draftRow, setDraftRow] = useState<StackerBundleDraft | null>(null);
	const [error, setError] = useState("");
	const [notificationMessage, setNotificationMessage] = useState("");
	const [bundleToDelete, setBundleToDelete] = useState<StackerBundle | null>(
		null,
	);

	const {
		data: report,
		isLoading,
		isError,
		error: e,
	} = useStackerReport({
		shiftId: currentShiftId,
	});

	const bundles = report?.bundles || [];
	const remark = report?.remark ?? null;

	const upsertBundle = useMutation(upsertStackerBundle, {
		onSuccess: () => {
			queryClient.invalidateQueries({
				queryKey: ["stacker-report", currentShiftId],
			});
			setNotificationMessage("Bundle saved successfully");
		},
		onError: (error: Error) => setError(error.message),
	});
	const deleteBundle = useMutation(deleteStackerBundle, {
		onSuccess: () => {
			queryClient.invalidateQueries({
				queryKey: ["stacker-report", currentShiftId],
			});
			setNotificationMessage("Bundle deleted successfully");
		},
		onError: (error: Error) => setError(error.message),
	});
	const upsertRemark = useMutation(upsertStackerShiftRemark, {
		onSuccess: () => {
			queryClient.invalidateQueries({
				queryKey: ["stacker-report", currentShiftId],
			});
			setNotificationMessage("Remark saved successfully");
		},
		onError: (error: Error) => setError(error.message),
	});

	const isBundleMutating = upsertBundle.isPending || deleteBundle.isPending;

	const startEdit = (b: StackerBundle) => {
		setEditingId(b.StackerBundleId);

		if (isM1) {
			setDraftRow({
				...STACKER_PIECES_DRAFT_INITIAL,
				StackerBundleId: b.StackerBundleId,
				ShiftId: b.ShiftId,
				SectionId: b.SectionId,
				GradeId: b.GradeId,
				HeatId: b.HeatId,
				LengthInches: b.LengthInches,
				GoodPieces: b.GoodPieces ?? 0,
				TotalWeight: b.TotalWeight,
				ScrapPieces: b.ScrapPieces ?? null,
				ScrapReason: b.ScrapReason,
			});
			return;
		}

		const auto = (b.GoodBundles || 0) * (b.GoodPiecesPerBundle || 0);
		setDraftRow({
			StackerBundleId: b.StackerBundleId,
			ShiftId: b.ShiftId,
			SectionId: b.SectionId,
			GradeId: b.GradeId,
			HeatId: b.HeatId,
			LengthInches: b.LengthInches,
			GoodBundles: b.GoodBundles,
			GoodPiecesPerBundle: b.GoodPiecesPerBundle,
			TotalPieces: b.TotalPieces,
			TotalPiecesIsManual: b.TotalPieces !== auto,
			TotalWeight: b.TotalWeight,
			ScrapBundles: b.ScrapBundles,
			ScrapPiecesPerBundle: b.ScrapPiecesPerBundle,
			ScrapReason: b.ScrapReason,
		});
	};

	const startAdd = () => {
		if (!currentShiftId) return;
		setEditingId(null);
		setDraftRow({
			...(isM1 ? STACKER_PIECES_DRAFT_INITIAL : STACKER_BUNDLE_DRAFT_INITIAL),
			ShiftId: currentShiftId,
		});
	};

	const cancelEdit = () => {
		setEditingId(null);
		setDraftRow(null);
	};

	useEffect(() => {
		if (shiftInfo) {
			const shiftId = get(shiftInfo, "CurrentShiftId", null);
			navigate(`${routeStackerProduction}/${shiftId}`, { replace: true });
			cancelEdit();
		}
	}, [shiftInfo]);

	useEffect(() => {
		if (shiftIdParam) {
			(async () => {
				const currentDate = shiftInfo?.CurrentProductionDate ?? null;
				await getShiftDataFromAPI(currentDate, 0, Number(defaultAreaId));
			})();
		} else {
			(async () => {
				const currentDate = shiftInfo?.CurrentProductionDate ?? null;
				await getShiftDataFromAPI(currentDate, 0, Number(defaultAreaId));
			})();
		}
	}, [defaultAreaId]);

	useEffect(() => {
		if (isError && e) {
			setError(e.message);
		}
	}, [e, isError]);

	return (
		<>
			<HelmetDexteel title="Stacker Production" />
			<Box
				sx={{
					backgroundColor: pageBgColor,
					minHeight: "calc(100vh - 70px)",
				}}
			>
				<Grid2 container size={{ md: 12, xs: 12 }} spacing={1} px={2} pt={2}>
					<Grid2 size={{ md: 12, xs: 12 }}>
						<Typography variant="h5" sx={{ fontWeight: 600 }}>
							Stacker Production
						</Typography>
					</Grid2>

					<Grid2
						container
						size={{ md: 12, xs: 12 }}
						alignItems="center"
						justifyContent="space-between"
						spacing={1}
					>
						<Grid2 size={{ xs: "auto" }}>
							<ShiftDayNavigatorControl
								preventInit
								assetId={Number(defaultAreaId)}
							/>
						</Grid2>
						<Grid2 size={{ xs: "auto" }}>
							<Button
								variant="contained"
								startIcon={<Add />}
								onClick={startAdd}
								disabled={
									!currentShiftId ||
									draftRow !== null ||
									editingId !== null ||
									isBundleMutating
								}
							>
								Add Row
							</Button>
						</Grid2>
					</Grid2>

					<Grid2 size={{ md: 12, xs: 12 }}>
						<Divider sx={{ my: 1 }} />
					</Grid2>

					<Grid2 size={{ md: 12, xs: 12 }}>
						{isLoading ? (
							<Box sx={{ height: 200 }}>
								<CenteredLazyLoading />
							</Box>
						) : (
							(() => {
								const StackerGrid = isM1
									? StackerPiecesGrid
									: StackerBundlesGrid;
								return (
									<StackerGrid
										rows={bundles}
										shiftId={currentShiftId || 0}
										draftRow={draftRow}
										editingId={editingId}
										isMutating={isBundleMutating}
										onStartEdit={startEdit}
										onCancelEdit={cancelEdit}
										onDraftChange={setDraftRow}
										onSaveDraft={(draft) => {
											if (draftRow) {
												upsertBundle.mutate({
													draft,
													defaultAreaId: Number(defaultAreaId),
												});
											}
											setDraftRow(null);
											setEditingId(null);
										}}
										onDelete={(s) => setBundleToDelete(s)}
									/>
								);
							})()
						)}
					</Grid2>

					<Grid2 size={{ md: 12, xs: 12 }} sx={{ mt: 2 }}>
						<StackerShiftRemarkField
							remark={remark}
							disabled={!currentShiftId}
							isMutating={upsertRemark.isLoading}
							onSave={(comment) => {
								if (!currentShiftId) return;
								upsertRemark.mutate({
									shiftId: currentShiftId,
									comment,
									defaultAreaId: Number(defaultAreaId),
								});
							}}
						/>
					</Grid2>
				</Grid2>
			</Box>

			<ConfirmationDialogRaw
				title="Confirm Deletion"
				message={
					bundleToDelete
						? `Are you sure you want to delete the bundle with section ${bundleToDelete.SectionName}, ${
								bundleToDelete.HeatName
									? `heat ${bundleToDelete.HeatName}, `
									: ""
							}${
								bundleToDelete.GradeName
									? `grade ${bundleToDelete.GradeName}, `
									: ""
							}length ${Math.floor(
								bundleToDelete.LengthInches / 12,
							)}' ${bundleToDelete.LengthInches % 12}" and ${
								bundleToDelete.TotalPieces ?? bundleToDelete.GoodPieces ?? 0
							} total pieces? This action cannot be undone.`
						: ""
				}
				open={!!bundleToDelete}
				onClose={(value?: string) => {
					if (value == "OK") {
						deleteBundle.mutate({
							stackerBundleId: bundleToDelete?.StackerBundleId!,
							defaultAreaId: Number(defaultAreaId),
						});
					}
					setBundleToDelete(null);
				}}
			/>
			<ErrorModal error={error} onHide={() => setError("")} />
			<NotificationSnackBar
				message={notificationMessage}
				onHide={() => setNotificationMessage("")}
			/>
		</>
	);
};
